import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GeoUtils, BusinessPhotoUtils, SpatialCache } from '../../utils/geospatial';
import { JobService, BusinessService, ApplicationService } from '../../services';

// Map container style
const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '8px',
  overflow: 'hidden'
};

// Default center (New York City)
const defaultCenter = {
  lat: 40.7128,
  lng: -74.0060
};

// Libraries to load with Google Maps (add places for autocomplete)
const libraries = ['places'];

// Custom marker component for salary pins with better performance
const SalaryPin = React.memo(({ hourlyRate, isWeekly, onClick, selected, businessId }) => {
  const formattedRate = isWeekly 
    ? `$${hourlyRate}`
    : (typeof hourlyRate === 'number' && hourlyRate > 0 ? `$${hourlyRate}` : '$0');
    
  return (
    <div 
      onClick={onClick}
      className={`cursor-pointer transition-all transform hover:scale-110 ${selected ? 'scale-110 z-50' : ''}`}
      style={{ willChange: 'transform' }}
    >
      <div 
        className={`flex items-center justify-center rounded-full 
        ${selected ? 'bg-blue-500' : 'bg-[#FF8C00]'} 
        text-white font-bold px-2 py-1 shadow-md min-w-[44px] h-[28px]`}
        style={{ 
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          border: selected ? '2px solid #2563eb' : '1px solid #E67A00'
        }}
      >
        {formattedRate}
      </div>
    </div>
  );
});

// Custom user location marker
const UserLocationMarker = React.memo(({ position }) => {
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(width, height) => ({
        x: -12,
        y: -12
      })}
    >
      <div className="relative">
        <div className="w-6 h-6 rounded-full bg-blue-500 border-2 border-white shadow-lg"></div>
        <div className="absolute top-0 left-0 w-6 h-6 rounded-full bg-blue-500 animate-ping opacity-75"></div>
      </div>
    </OverlayView>
  );
});

const OptimizedJobMap = () => {
  // Auth context
  const { currentUser } = useAuth();
  
  // State
  const [visibleBusinesses, setVisibleBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(12);
  const [currentBounds, setCurrentBounds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [error, setError] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [payRange, setPayRange] = useState(10);
  const [geocoder, setGeocoder] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Refs and instances
  const mapRef = useRef(null);
  const spatialCache = useRef(new SpatialCache());
  const fetchTimeoutRef = useRef(null);
  const lastFetchParamsRef = useRef(null);
  const locationInputRef = useRef(null);
  
  // Navigation
  const navigate = useNavigate();

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  // Debug: Check if API key is loaded (remove this after testing)
  useEffect(() => {
    console.log('Google Maps API Key status:', process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? 'Loaded' : 'Missing');
  }, []);

  // Advanced geospatial fetch function that gets ALL businesses in viewport
  const fetchBusinessesInViewport = useCallback(async (bounds, zoom) => {
    if (!bounds || isFetchingData) return;

    const cacheKey = spatialCache.current.generateKey(bounds, zoom);
    
    // Check cache first
    const cachedData = spatialCache.current.get(cacheKey);
    if (cachedData) {
      console.log("📊 Using cached data for viewport");
      setVisibleBusinesses(cachedData);
      return;
    }

    // Check if we need to fetch based on significant change
    const currentParams = { bounds: bounds.toString(), zoom };
    if (lastFetchParamsRef.current && 
        JSON.stringify(currentParams) === JSON.stringify(lastFetchParamsRef.current)) {
      return;
    }

    try {
      setIsFetchingData(true);
      setLoading(true);
      setLoadingProgress(20);

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      
      // Get pin density configuration based on zoom
      const { maxPins, gridSize } = GeoUtils.getPinDensity(zoom);
      
      console.log(`🔍 Fetching ALL businesses in viewport: zoom=${zoom}, bounds: [${sw.lat().toFixed(4)}, ${sw.lng().toFixed(4)}] to [${ne.lat().toFixed(4)}, ${ne.lng().toFixed(4)}]`);

      setLoadingProgress(30);

      // Use BusinessService to fetch businesses in bounds
      const boundsObject = {
        ne: { lat: ne.lat(), lng: ne.lng() },
        sw: { lat: sw.lat(), lng: sw.lng() }
      };
      
      const businessesInBounds = await BusinessService.fetchBusinessesInBounds(boundsObject, maxPins);
      
      setLoadingProgress(50);
      
      console.log(`📊 Found ${businessesInBounds.length} businesses in exact viewport bounds`);

      setLoadingProgress(60);

      // Fetch jobs for businesses using JobService FIRST
      if (businessesInBounds.length > 0) {
        const businessIds = businessesInBounds.map(b => b.id);
        console.log(`🔍 Fetching jobs for ${businessIds.length} businesses:`, businessIds);
        
        const jobsByBusiness = await JobService.fetchJobsForBusinesses(businessIds);
        console.log(`📊 JobService returned jobs for businesses:`, Object.keys(jobsByBusiness).length, 'businesses with jobs');
        console.log(`📊 Jobs data sample:`, Object.keys(jobsByBusiness).slice(0, 3).map(id => ({
          businessId: id, 
          jobCount: jobsByBusiness[id]?.length || 0,
          jobs: jobsByBusiness[id]?.slice(0, 2)
        })));
        
        // Debug: Check for any jobs at all
        const totalJobs = Object.values(jobsByBusiness).reduce((total, jobs) => total + (jobs?.length || 0), 0);
        console.log(`🎯 Total jobs found across all businesses: ${totalJobs}`);
        
        if (totalJobs === 0) {
          console.warn('⚠️ No jobs found for any businesses! This could indicate:');
          console.warn('   1. businessId mismatch between businesses and jobs collections');
          console.warn('   2. All jobs have isActive: false');
          console.warn('   3. Different field names in jobs collection');
          console.warn('   4. Database connection issues');
        }
        
        setLoadingProgress(70);

        // Process businesses with jobs and images using BusinessService
        const processedBusinesses = await Promise.all(
          businessesInBounds.map(async (business) => {
            business.jobs = jobsByBusiness[business.id] || [];
            console.log(`📍 Processing business ${business.name}: ${business.jobs.length} jobs found`);
            
            // Get business image with automatic generation using BusinessService
            business.imageUrl = await BusinessService.getBusinessImageUrl(
              business.businessImage?.path, 
              business.name
            );
            
            // Sort jobs by hourly rate (handled by JobService now)
            if (business.jobs.length > 0) {
              business.jobs.sort((a, b) => (b.hourlyRate || 0) - (a.hourlyRate || 0));
              console.log(`📍 Business ${business.name} top job: ${business.jobs[0].title} - $${business.jobs[0].hourlyRate || 0}/hr`);
            }
            
            return business;
          })
        );

        console.log(`📊 Processed businesses summary:`, processedBusinesses.map(b => ({
          name: b.name,
          jobCount: b.jobs.length,
          topSalary: b.jobs[0]?.hourlyRate || 0
        })));

        setLoadingProgress(80);

        // Apply spatial clustering AFTER jobs are fetched (if needed)
        let finalBusinesses = processedBusinesses;
        if (processedBusinesses.length > maxPins) {
          console.log(`🎯 Applying spatial clustering: ${processedBusinesses.length} → ${maxPins} businesses`);
          finalBusinesses = applySpatialClustering(processedBusinesses, gridSize, maxPins);
        }

        console.log(`📊 Loaded ${finalBusinesses.length} businesses for viewport`);
        
        // Cache the results
        spatialCache.current.set(cacheKey, finalBusinesses);
        
        setVisibleBusinesses(finalBusinesses);
        lastFetchParamsRef.current = currentParams;
      }

      setLoadingProgress(100);
      setLoading(false);
      setIsFetchingData(false);

    } catch (error) {
      console.error("Error fetching viewport data:", error);
      setError(`Failed to load businesses: ${error.message}`);
      setLoading(false);
      setIsFetchingData(false);
      setLoadingProgress(0);
    }
  }, [isFetchingData]);

  // Spatial clustering to reduce pin density at higher zoom levels
  const applySpatialClustering = (businesses, gridSize, maxPins) => {
    const clusters = new Map();
    
    businesses.forEach(business => {
      const lat = business.address.coordinates.latitude;
      const lng = business.address.coordinates.longitude;
      
      // Create grid cell key
      const cellLat = Math.floor(lat / gridSize) * gridSize;
      const cellLng = Math.floor(lng / gridSize) * gridSize;
      const cellKey = `${cellLat},${cellLng}`;
      
      if (!clusters.has(cellKey)) {
        clusters.set(cellKey, []);
      }
      clusters.get(cellKey).push(business);
    });
    
    // Select representative business from each cluster
    const clusteredBusinesses = [];
    for (const [cellKey, cellBusinesses] of clusters) {
      // Sort by number of jobs and pick the business with most jobs
      cellBusinesses.sort((a, b) => (b.jobs?.length || 0) - (a.jobs?.length || 0));
      clusteredBusinesses.push(cellBusinesses[0]);
      
      if (clusteredBusinesses.length >= maxPins) break;
    }
    
    return clusteredBusinesses;
  };

  // Handle map viewport changes with debouncing
  const handleViewportChange = useCallback(() => {
    if (!mapRef.current || isFetchingData) return;

    const bounds = mapRef.current.getBounds();
    const zoom = mapRef.current.getZoom();
    
    if (bounds && zoom) {
      setCurrentBounds(bounds);
      setCurrentZoom(zoom);
      
      // Debounce the fetch to avoid excessive API calls
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      fetchTimeoutRef.current = setTimeout(() => {
        fetchBusinessesInViewport(bounds, zoom);
      }, 300);
    }
  }, [fetchBusinessesInViewport, isFetchingData]);

  // Initialize geocoder after map loads
  useEffect(() => {
    if (isLoaded && window.google) {
      setGeocoder(new window.google.maps.Geocoder());
      
      // Initialize Places Autocomplete
      if (locationInputRef.current && !autocomplete) {
        const autocompleteInstance = new window.google.maps.places.Autocomplete(
          locationInputRef.current,
          {
            types: ['(cities)'], // Restrict to cities and places
            componentRestrictions: { country: ['us', 'ca'] }, // Restrict to US and Canada
            fields: ['place_id', 'formatted_address', 'geometry', 'name']
          }
        );

        // Set up autocomplete event listener
        autocompleteInstance.addListener('place_changed', () => {
          const place = autocompleteInstance.getPlace();
          
          if (place.geometry && place.geometry.location) {
            console.log('📍 Location selected from autocomplete:', place.formatted_address);
            
            // Hide popular locations dropdown
            setShowLocationSuggestions(false);
            
            // Update the input value
            setLocationQuery(place.formatted_address || place.name);
            
            // Move map to selected location
            const newCenter = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            
            if (mapRef.current) {
              mapRef.current.panTo(newCenter);
              mapRef.current.setZoom(14);
              
              // Clear cache when location changes
              spatialCache.current.clear();
              
              // Trigger data refresh
              setTimeout(() => {
                handleViewportChange();
              }, 500);
            }
          } else {
            console.warn('No geometry data for selected place');
          }
        });

        setAutocomplete(autocompleteInstance);
      }
    }
  }, [isLoaded, autocomplete, handleViewportChange]);

  // Clear cache on component mount to ensure fresh data
  useEffect(() => {
    spatialCache.current.clear();
    console.log('🧹 Cleared spatial cache on component mount');
  }, []);

  // Get user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("📍 User location obtained");
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log("📍 Geolocation error, using default location (New York):", error.message);
          setUserLocation(defaultCenter);
        }
      );
    } else {
      console.log("📍 Geolocation not supported, using default location (New York)");
      setUserLocation(defaultCenter);
    }
  }, []);

  // Check which jobs the user has already applied to
  const checkAppliedJobs = useCallback(async () => {
    if (!currentUser || visibleBusinesses.length === 0) return;

    try {
      // Get all job IDs from visible businesses
      const allJobIds = [];
      visibleBusinesses.forEach(business => {
        business.jobs.forEach(job => {
          allJobIds.push(job.id);
        });
      });

      // Use ApplicationService for batch checking
      const appliedJobIds = await ApplicationService.batchCheckApplicationStatus(currentUser.uid, allJobIds);
      setAppliedJobs(appliedJobIds);
    } catch (error) {
      console.error('Error checking applied jobs:', error);
    }
  }, [currentUser, visibleBusinesses]);

  // Apply filters to businesses
  const applyFilters = useCallback(() => {
    let filtered = [...visibleBusinesses];
    
    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(business => {
        // Search in business name
        if (business.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in job titles and descriptions
        const hasMatchingJobs = business.jobs.some(job => 
          job.title?.toLowerCase()?.includes(query) || 
          (job.description && job.description.toLowerCase().includes(query))
        );
        
        return hasMatchingJobs;
      });
    }
    
    // Filter businesses that have jobs meeting the pay range (only if pay range is changed from default)
    if (payRange > 10 && searchQuery.trim() === '') {
      filtered = filtered.map(business => {
        const filteredJobs = business.jobs.filter(job => 
          (job.hourlyRate || 0) >= payRange
        );
        
        return {
          ...business,
          jobs: filteredJobs
        };
      });
      
      // Only filter out businesses without matching jobs when pay filter is active
      filtered = filtered.filter(business => business.jobs.length > 0);
    }
    
    // Only filter out businesses without jobs when actively searching
    if (searchQuery.trim() !== '') {
      filtered = filtered.filter(business => business.jobs.length > 0);
    }
    
    setFilteredBusinesses(filtered);
  }, [visibleBusinesses, searchQuery, payRange]);

  // Apply filters whenever search query or pay range changes
  useEffect(() => {
    if (visibleBusinesses.length > 0) {
      applyFilters();
      if (currentUser) {
        checkAppliedJobs();
      }
    }
  }, [searchQuery, payRange, visibleBusinesses, currentUser, applyFilters, checkAppliedJobs]);

  // Search for location and move map
  const searchLocation = (e) => {
    e.preventDefault();
    
    if (!geocoder || !locationQuery.trim()) return;
    
    setLoading(true);
    setLoadingProgress(10);
    
    // Use geocoder for manual searches (when autocomplete wasn't used)
    geocoder.geocode({ address: locationQuery }, (results, status) => {
      if (status === 'OK' && results[0] && mapRef.current) {
        const location = results[0].geometry.location;
        const newCenter = {
          lat: location.lat(),
          lng: location.lng()
        };
        
        console.log('📍 Manual location search result:', results[0].formatted_address);
        
        mapRef.current.panTo(newCenter);
        mapRef.current.setZoom(14);
        
        // Clear cache when searching new location
        spatialCache.current.clear();
        
        // Trigger data refresh
        setTimeout(() => {
          handleViewportChange();
        }, 500);
        
      } else {
        setError(`Could not find location: ${locationQuery}`);
        setLoading(false);
        setLoadingProgress(0);
      }
    });
  };

  // Handle map load
  const handleMapLoad = useCallback(map => {
    console.log("🗺️ Map loaded successfully");
    mapRef.current = map;
    setMap(map);

    // Add optimized event listeners
    map.addListener('idle', handleViewportChange);
    map.addListener('zoom_changed', handleViewportChange);

    // Set initial center
    if (userLocation) {
      map.setCenter(userLocation);
    }

    // Initial data fetch
    setTimeout(() => {
      handleViewportChange();
    }, 1000);
  }, [userLocation, handleViewportChange]);

  // Handle map unmount
  const handleMapUnmount = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    mapRef.current = null;
    setMap(null);
  }, []);

  // Handle business selection
  const handleSelectBusiness = useCallback((business) => {
    setSelectedBusiness(business);
  }, []);

  // Handle closing business details
  const handleCloseDetails = useCallback(() => {
    setSelectedBusiness(null);
  }, []);

  // Handle "Apply Now" button click with proper authentication and application tracking
  const handleApplyNow = useCallback(async (businessId, jobId, job) => {
    try {
      // Check if user is authenticated
      if (!currentUser) {
        // Redirect to login with return URL
        navigate(`/login?redirect=/dashboard/map`);
        return;
      }

      // Check if job has external application URL
      if (job?.applicationUrl) {
        await ApplicationService.handleExternalApplication(currentUser.uid, jobId, job.applicationUrl);
        return;
      }

      // Submit application using ApplicationService
      const result = await ApplicationService.submitApplication({
        userId: currentUser.uid,
        jobId: jobId,
        businessId: businessId
      });

      if (result.success) {
        // Update local state to reflect the application
        setAppliedJobs(prev => new Set([...prev, jobId]));
        // alert(result.message); // Disabled notification
      } else {
        alert(result.message);
      }
      
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    }
  }, [navigate, currentUser]);

  // Handle viewing job details 
  const handleViewJobDetails = useCallback((jobId) => {
    navigate(`/jobs/${jobId}`);
  }, [navigate]);

  // Handle clicking on business name to view business details
  const handleViewBusinessDetails = useCallback((businessId) => {
    navigate(`/businesses/${businessId}`);
  }, [navigate]);

  // Center map on user's location
  const handleCenterOnUser = useCallback(() => {
    if (userLocation && mapRef.current) {
      mapRef.current.panTo(userLocation);
      mapRef.current.setZoom(14);
    }
  }, [userLocation]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      spatialCache.current.clear();
      
      // Clean up autocomplete
      if (autocomplete) {
        window.google?.maps?.event?.clearInstanceListeners(autocomplete);
      }
    };
  }, [autocomplete]);

  // Add custom styles for Google Places autocomplete
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        border-radius: 8px !important;
        border: 1px solid #d1d5db !important;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        font-family: inherit !important;
        margin-top: 4px !important;
      }
      .pac-item {
        padding: 12px 16px !important;
        border-bottom: 1px solid #f3f4f6 !important;
        font-size: 14px !important;
        cursor: pointer !important;
      }
      .pac-item:hover {
        background-color: #f9fafb !important;
      }
      .pac-item-selected {
        background-color: #fff7ed !important;
      }
      .pac-matched {
        color: #FF8C00 !important;
        font-weight: 600 !important;
      }
      .pac-item-query {
        color: #374151 !important;
        font-size: 14px !important;
      }
      .pac-icon {
        margin-top: 2px !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Popular location suggestions
  const popularLocations = [
    { name: 'New York, NY', coords: { lat: 40.7128, lng: -74.0060 } },
    { name: 'Los Angeles, CA', coords: { lat: 34.0522, lng: -118.2437 } },
    { name: 'Chicago, IL', coords: { lat: 41.8781, lng: -87.6298 } },
    { name: 'Houston, TX', coords: { lat: 29.7604, lng: -95.3698 } },
    { name: 'Phoenix, AZ', coords: { lat: 33.4484, lng: -112.0740 } },
    { name: 'Philadelphia, PA', coords: { lat: 39.9526, lng: -75.1652 } },
    { name: 'San Antonio, TX', coords: { lat: 29.4241, lng: -98.4936 } },
    { name: 'San Diego, CA', coords: { lat: 32.7157, lng: -117.1611 } }
  ];

  // Handle popular location selection
  const handlePopularLocationClick = (location) => {
    setLocationQuery(location.name);
    setShowLocationSuggestions(false);
    
    if (mapRef.current) {
      mapRef.current.panTo(location.coords);
      mapRef.current.setZoom(12);
      
      // Clear cache when location changes
      spatialCache.current.clear();
      
      // Trigger data refresh
      setTimeout(() => {
        handleViewportChange();
      }, 500);
    }
  };

  if (!isLoaded) return <div className="p-4">Loading maps...</div>;

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="ml-4 text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 max-w-7xl mx-auto">
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 text-left">Optimized Job Map</h1>
        <p className="text-sm sm:text-base lg:text-lg text-gray-600 text-left">
          Find jobs near you with smart loading - zoom in for more detail
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-6 h-[calc(100vh-200px)] sm:h-[calc(100vh-180px)] lg:h-[calc(100vh-160px)]">
        {/* Sidebar */}
        <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 bg-white rounded-lg border border-gray-200 p-2 sm:p-3 lg:p-4 overflow-y-auto h-[280px] lg:h-full">
          {/* Search */}
          <div className="mb-2 sm:mb-3 lg:mb-4">
            <h3 className="text-xs sm:text-sm lg:text-base mb-1 text-left font-medium">Search jobs</h3>
            <form onSubmit={(e) => { e.preventDefault(); applyFilters(); }}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Job title, company, or keywords"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg py-1.5 sm:py-2 pl-7 sm:pl-8 lg:pl-10 pr-3 sm:pr-4 text-left text-xs sm:text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                />
                <svg 
                  className="absolute left-2 sm:left-2.5 lg:left-3 top-2 w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-gray-500"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </form>
          </div>

          {/* Location Search */}
          <div className="mb-2 sm:mb-3 lg:mb-4">
            <h3 className="text-xs sm:text-sm lg:text-base mb-1 text-left font-medium">Location</h3>
            <form onSubmit={searchLocation}>
              <div className="relative">
                <input
                  ref={locationInputRef}
                  type="text"
                  placeholder="Enter city, address, or zip code"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  onFocus={() => setShowLocationSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  className="w-full border border-gray-300 rounded-lg py-1.5 sm:py-2 pl-7 sm:pl-8 lg:pl-10 pr-8 sm:pr-10 lg:pr-12 text-left focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-xs sm:text-sm lg:text-base"
                  autoComplete="off"
                />
                <svg 
                  className="absolute left-2 sm:left-2.5 lg:left-3 top-2 w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-gray-500"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <button 
                  type="submit"
                  className="absolute right-1 sm:right-1.5 lg:right-2 top-1 px-1 sm:px-1.5 lg:px-2 py-0.5 text-[#FF8C00] hover:text-[#FF8C00]/80 text-xs font-medium rounded transition-colors"
                  title="Search this location"
                >
                  Go
                </button>
                
                {/* Popular Locations Dropdown */}
                {showLocationSuggestions && !locationQuery && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-32 sm:max-h-40 overflow-y-auto">
                    <div className="p-1.5 sm:p-2 border-b border-gray-100">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Popular Cities</span>
                    </div>
                    {popularLocations.map((location, index) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-slate-50 flex items-center text-xs sm:text-sm"
                        onClick={() => handlePopularLocationClick(location)}
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-gray-700">{location.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </form>
            <p className="text-xs text-gray-500 mt-0.5 text-left">
              {locationQuery ? 'Google suggestions will appear' : 'Click to see popular cities'}
            </p>
          </div>
          
          {/* Pay range */}
          <div className="mb-3 sm:mb-4">
            <div className="flex justify-between mb-1">
              <h3 className="text-xs sm:text-sm lg:text-base text-left font-medium">Pay range</h3>
              <span className="font-medium text-right text-xs sm:text-sm lg:text-base">${payRange}</span>
            </div>
            <input 
              type="range"
              min="10"
              max="100"
              value={payRange}
              onChange={(e) => setPayRange(parseInt(e.target.value))}
              className="w-full h-1.5 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF8C00]"
            />
          </div>
          
          {/* Results info */}
          <div className="bg-slate-50 border border-gray-200 rounded-lg p-2 mb-3 sm:mb-4">
            <p className="text-xs sm:text-sm text-gray-700 text-left">
              <span className="font-bold text-[#FF8C00]">{filteredBusinesses.length}</span> businesses
              {searchQuery && <> matching "<span className="font-semibold">{searchQuery}</span>"</>}
            </p>
            <p className="text-xs sm:text-sm text-gray-700 text-left mt-0.5">
              <span className="font-bold text-[#FF8C00]">
                {filteredBusinesses.reduce((total, business) => total + business.jobs.length, 0)}
              </span> jobs • Zoom {currentZoom}
            </p>
          </div>
          
          {/* My Location Button */}
          <button 
            onClick={handleCenterOnUser}
            className="flex items-center justify-center w-full py-1.5 sm:py-2 border border-gray-300 rounded-lg hover:bg-slate-50 text-gray-700 transition-colors text-xs sm:text-sm lg:text-base"
            disabled={!userLocation}
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 7c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
            </svg>
            Center On My Location
          </button>
        </div>
        
        {/* Map Container */}
        <div className="flex-1 relative rounded-lg border border-gray-200 overflow-hidden">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={userLocation || defaultCenter}
            zoom={12}
            onLoad={handleMapLoad}
            onUnmount={handleMapUnmount}
            options={{
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: false,
              zoomControl: true,
              zoomControlOptions: {
                position: window.google?.maps?.ControlPosition?.RIGHT_CENTER || 6
              }
            }}
          >
            {/* User Location Marker */}
            {userLocation && (
              <UserLocationMarker position={userLocation} />
            )}

            {/* Business Markers */}
            {filteredBusinesses.map(business => {
              if (!business.address?.coordinates) return null;
              
              const position = {
                lat: business.address.coordinates.latitude,
                lng: business.address.coordinates.longitude
              };

              const highestPayingJob = business.jobs?.[0] || { hourlyRate: 0 };
              const hourlyRate = highestPayingJob.hourlyRate || 0;
              const isWeekly = highestPayingJob.paymentType === 'weekly';
              const isSelected = selectedBusiness && selectedBusiness.id === business.id;

              return (
                <OverlayView
                  key={business.id}
                  position={position}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -width / 2,
                    y: -height / 2
                  })}
                >
                  <SalaryPin 
                    businessId={business.id}
                    hourlyRate={business.jobs.length > 0 ? (isWeekly ? hourlyRate*40 : hourlyRate) : 0}
                    isWeekly={isWeekly}
                    onClick={() => handleSelectBusiness(business)}
                    selected={isSelected}
                  />
                </OverlayView>
              );
            })}
          </GoogleMap>

          {/* Loading Indicator */}
          {loading && (
            <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-white p-1.5 sm:p-2 rounded-lg shadow-md z-10 flex items-center">
              <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-[#FF8C00] border-t-transparent rounded-full"></div>
              <span className="text-xs sm:text-sm text-gray-600 ml-1.5 sm:ml-2">
                Loading...
              </span>
            </div>
          )}

          {/* My Location Button */}
          <button
            onClick={handleCenterOnUser}
            className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-white p-1.5 sm:p-2 rounded-full shadow-md hover:shadow-lg z-10 transition-all"
            title="Center on my location"
            disabled={!userLocation}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 7c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
            </svg>
          </button>

          {/* Selected Business Panel */}
          {selectedBusiness && (
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-2 sm:p-3 lg:p-4 z-20 max-h-[45%] sm:max-h-[50%] lg:max-h-[55%] overflow-y-auto shadow-lg">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <div className="flex items-center mb-1.5 sm:mb-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 rounded-md mr-2 overflow-hidden flex-shrink-0">
                      {selectedBusiness.imageUrl ? (
                        <img 
                          src={selectedBusiness.imageUrl} 
                          alt={selectedBusiness.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div 
                          style={BusinessPhotoUtils.generateInlineStyle(selectedBusiness.name)}
                          className="w-full h-full text-xs flex items-center justify-center font-bold"
                        >
                          {BusinessPhotoUtils.generateInitials(selectedBusiness.name)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <button 
                        onClick={() => handleViewBusinessDetails(selectedBusiness.id)}
                        className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 hover:text-[#FF8C00] transition-colors cursor-pointer text-left block"
                      >
                        {selectedBusiness.name}
                      </button>
                      <p className="text-xs text-gray-600">
                        {selectedBusiness.address?.street}, {selectedBusiness.address?.city}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <span className="flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7h-3V6a3 3 0 00-3-3h-4a3 3 0 00-3 3v1H4a1 1 0 00-1 1v11a3 3 0 003 3h12a3 3 0 003-3V8a1 1 0 00-1-1zM9 6a1 1 0 011-1h4a1 1 0 011 1v1H9V6z" />
                      </svg>
                      {selectedBusiness.jobs.length} position{selectedBusiness.jobs.length !== 1 ? 's' : ''}
                    </span>
                    {selectedBusiness.businessType && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {selectedBusiness.businessType}
                      </span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={handleCloseDetails}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Jobs List - Enhanced */}
              {selectedBusiness.jobs.length > 0 && (
                <div className="space-y-2">
                  {selectedBusiness.jobs.slice(0, 2).map((job, index) => (
                    <div key={job.id} className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all duration-200 hover:from-blue-50 hover:to-indigo-50">
                      {/* Job Header with Full Info */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 mr-3">
                          <button 
                            onClick={() => handleViewJobDetails(job.id)}
                            className="text-base sm:text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors cursor-pointer text-left line-clamp-1 block w-full mb-1"
                          >
                            {job.title}
                          </button>
                          
                          {/* Job Meta Info */}
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {job.jobType || 'Full-time'}
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {job.startTime || 'Immediate'}
                            </span>
                            {job.applicationCount > 0 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {job.applicationCount} applied
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Enhanced Salary Display */}
                        <div className="flex-shrink-0 text-center">
                          <div className="bg-gradient-to-r from-[#FF8C00] to-[#FF7700] text-white px-3 py-2 rounded-lg shadow-sm">
                            <div className="text-base sm:text-lg font-bold">${job.hourlyRate || 0}</div>
                            <div className="text-xs opacity-90">per hour</div>
                          </div>
                          {job.extraCompensations && job.extraCompensations.length > 0 && (
                            <div className="text-xs text-green-600 mt-1 font-medium">
                              + Benefits
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Job Description */}
                      {job.description && (
                        <p className="text-sm text-gray-700 mb-2 line-clamp-2 leading-relaxed">
                          {job.description}
                        </p>
                      )}

                      {/* Required Skills */}
                      {job.requiredSkills && job.requiredSkills.length > 0 && (
                        <div className="mb-3">
                          <div className="flex items-center mb-1">
                            <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-600">Required Skills:</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {job.requiredSkills.slice(0, 4).map((skill, skillIndex) => (
                              <span key={skillIndex} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                                {skill}
                              </span>
                            ))}
                            {job.requiredSkills.length > 4 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500">
                                +{job.requiredSkills.length - 4} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Location and Work Info */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pt-2 border-t border-gray-200">
                        <div className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          On-site • {selectedBusiness.address?.city}
                        </div>
                        {job.extraCompensations && job.extraCompensations.length > 0 && (
                          <div className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                            {job.extraCompensations.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>

                      {/* Enhanced Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewJobDetails(job.id)}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-slate-50 hover:border-gray-400 transition-all flex-1 justify-center shadow-sm"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Details
                        </button>
                        
                        <button
                          onClick={() => handleApplyNow(selectedBusiness.id, job.id, job)}
                          disabled={appliedJobs.has(job.id)}
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all flex-1 justify-center shadow-sm ${
                            appliedJobs.has(job.id)
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-gradient-to-r from-[#FF8C00] to-[#FF7700] text-white hover:from-[#FF7700] hover:to-[#FF6600] transform hover:scale-105'
                          }`}
                        >
                          {appliedJobs.has(job.id) ? (
                            <>
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Applied
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                              {job.applicationUrl ? 'Apply on Website' : 'Apply Now'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* More Jobs Indicator */}
                  {selectedBusiness.jobs.length > 2 && (
                    <div className="text-center py-2 border-t border-gray-200">
                      <button 
                        onClick={() => handleViewBusinessDetails(selectedBusiness.id)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-[#FF8C00] hover:text-[#FF7700] hover:bg-orange-50 rounded-md transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        View all {selectedBusiness.jobs.length} positions at {selectedBusiness.name}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OptimizedJobMap; 